import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Users as UsersIcon,
  Package,
  Copy,
  Trash2,
} from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import VaulDrawer from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useRegion } from "@/features/regions/hooks/use-region";
import RegionForm from "@/features/regions/ui/region-form";
import RegionUserAnalytics from "@/features/regions/ui/region-user-analytics";
import RegionCloneStep from "@/features/regions/ui/region-clone-step";
import { useCloneRegion } from "@/features/regions/hooks/use-clone-region";
import {
  useRemoveAllRegionProducts,
  useRemoveRegionProduct,
} from "@/features/regions/hooks/use-region-products";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { getUsers } from "@/features/users/services/get-users";
import { getBundlesGrouped } from "@/features/bundles/services/get-bundles-grouped";

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5 truncate">{value || "—"}</p>
    </div>
  );
}

export default function RegionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: region, isLoading, isFetching, error } = useRegion(id);
  const code = region?.code;

  const [showEdit, setShowEdit] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState("");
  const [clonePrices, setClonePrices] = useState<Record<string, number>>({});
  const [cloneProductIds, setCloneProductIds] = useState<string[]>([]);
  const clone = useCloneRegion();

  // Destroy target: a specific product, or "ALL", or null (closed).
  const [destroy, setDestroy] = useState<
    { productId: string; name: string } | "ALL" | null
  >(null);
  const removeProduct = useRemoveRegionProduct();
  const removeAll = useRemoveAllRegionProducts();
  const destroyPending = removeProduct.isPending || removeAll.isPending;

  const { data: usersData } = useQuery({
    queryKey: ["users", "by-region", code],
    queryFn: () => getUsers({ page: 0, region: code }),
    enabled: !!code,
  });
  const { data: grouped } = useQuery({
    queryKey: ["bundles", "grouped", { region: code }],
    queryFn: () => getBundlesGrouped({ region: code! }),
    enabled: !!code,
  });

  const liveProducts = (grouped?.groups ?? [])
    .filter((g) => g.product && g.offerings.some((o) => o.current))
    .map((g) => ({
      id: g.product!.id,
      name: g.product!.name,
      offerings: g.offerings.filter((o) => o.current).length,
    }));

  const runDestroy = () => {
    if (!region || !destroy) return;
    const onResult = {
      onSuccess: (res: { deleted: number; deactivated: number }) => {
        toast({
          title: "Removed from region",
          description: `${res.deleted} deleted, ${res.deactivated} deactivated.`,
        });
        setDestroy(null);
      },
      onError: (e: any) =>
        toast({
          title: "Error",
          description: e.response?.data?.message || "Failed to remove",
          variant: "destructive",
        }),
    };
    if (destroy === "ALL") {
      removeAll.mutate(region.id, onResult);
    } else {
      removeProduct.mutate(
        { regionId: region.id, productId: destroy.productId },
        onResult,
      );
    }
  };

  const runClone = () => {
    if (!region || !cloneSourceId) return;
    clone.mutate(
      {
        sourceRegionId: cloneSourceId,
        targetRegionId: region.id,
        priceOverrides: clonePrices,
        productIds: cloneProductIds,
      },
      {
        onSuccess: (res) => {
          toast({
            title: "Products cloned",
            description: `${res.bundlesCreated} bundle(s), ${res.paymentPagesCreated} page(s), ${res.configsCreated} config(s) created.`,
          });
          setShowClone(false);
          setCloneSourceId("");
        },
        onError: (e: any) =>
          toast({
            title: "Error",
            description: e.response?.data?.message || "Failed to clone",
            variant: "destructive",
          }),
      },
    );
  };

  return (
    <ContentLayout
      data={region ? [region] : []}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      contentClassName="space-y-6"
      errorMessage="Failed to load region"
      emptyState={{ message: "Region not found" }}
    >
      {region && (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("..")}
                className="mb-1 -ml-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                {codeToFlag(region.code)} {region.display_name}
                <span className="text-sm font-mono text-gray-400">
                  {region.code}
                </span>
              </h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={region.is_active ? "active" : "inactive"}>
                  {region.is_active ? "ACTIVE" : "INACTIVE"}
                </Badge>
                {region.is_fallback && (
                  <Badge variant="secondary">Fallback · ROW</Badge>
                )}
                <Badge variant="outline">{region.currency}</Badge>
              </div>
            </div>
            <HasAccess permission="write">
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setShowClone(true)}>
                  <Copy className="h-4 w-4 mr-1" /> Clone products here
                </Button>
                <Button variant="outline" onClick={() => setShowEdit(true)}>
                  Edit
                </Button>
              </div>
            </HasAccess>
          </div>

          {/* Overview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Meta label="Code" value={region.code} />
                <Meta label="Currency" value={region.currency} />
                <Meta label="Timezone" value={region.timezone} />
                <Meta label="Locale" value={region.default_locale} />
                <Meta
                  label="Price floor (minor)"
                  value={region.price_floor_minor ?? "—"}
                />
                <Meta
                  label="Price ceiling (minor)"
                  value={region.price_ceiling_minor ?? "—"}
                />
                <Meta label="Fallback" value={region.is_fallback ? "Yes" : "No"} />
                <Meta
                  label="Status"
                  value={region.is_active ? "Active" : "Inactive"}
                />
              </div>
            </CardContent>
          </Card>

          {/* New-user analytics */}
          <RegionUserAnalytics regionId={region.id} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Users */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-gray-400" />
                  Users ({usersData?.total ?? 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {usersData?.users?.length ? (
                  <div className="border border-gray-200 rounded-md divide-y">
                    {usersData.users.slice(0, 5).map((u: any) => (
                      <div key={u.id} className="px-3 py-2 text-sm">
                        <p className="font-medium">{u.full_name}</p>
                        <p className="text-xs text-gray-400">
                          {u.preferred_email || u.emails?.[0] || "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No users in this region.</p>
                )}
                <Button
                  variant="link"
                  className="px-0 mt-2 h-auto"
                  onClick={() => navigate(`/${region.code}/users`)}
                >
                  View all users →
                </Button>
              </CardContent>
            </Card>

            {/* Products live */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    Products live ({liveProducts.length})
                  </CardTitle>
                  {liveProducts.length > 0 && (
                    <HasAccess permission="write">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 h-7"
                        onClick={() => setDestroy("ALL")}
                      >
                        Remove all
                      </Button>
                    </HasAccess>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {liveProducts.length ? (
                  <div className="border border-gray-200 rounded-md divide-y">
                    {liveProducts.map((p) => (
                      <div
                        key={p.id}
                        className="px-3 py-2 text-sm flex items-center justify-between gap-2"
                      >
                        <span className="flex-1 truncate">{p.name}</span>
                        <span className="text-xs text-gray-400">
                          {p.offerings} offering(s)
                        </span>
                        <HasAccess permission="write">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Remove from region"
                            onClick={() =>
                              setDestroy({ productId: p.id, name: p.name })
                            }
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </HasAccess>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    No products live in this region yet.
                  </p>
                )}
                <Button
                  variant="link"
                  className="px-0 mt-2 h-auto"
                  onClick={() => navigate(`/${region.code}/bundles`)}
                >
                  View bundles →
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Edit drawer */}
          <HasAccess permission="write">
            <VaulDrawer
              drawerDirection="right"
              isOpen={showEdit}
              onOpenChange={setShowEdit}
              triggerChildren={null}
              contentChildren={
                <RegionForm
                  editMode
                  initialData={region}
                  onClose={() => setShowEdit(false)}
                />
              }
            />
          </HasAccess>

          {/* Clone-into-this-region dialog */}
          <Dialog open={showClone} onOpenChange={setShowClone}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Clone products into {region.display_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <RegionCloneStep
                  targetRegionId={region.id}
                  sourceId={cloneSourceId}
                  onSourceChange={setCloneSourceId}
                  onPricesChange={setClonePrices}
                  onSelectionChange={setCloneProductIds}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowClone(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={runClone}
                    disabled={
                      !cloneSourceId ||
                      cloneProductIds.length === 0 ||
                      clone.isPending
                    }
                  >
                    {clone.isPending ? "Cloning…" : "Clone products"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Destroy confirm */}
          <Dialog open={!!destroy} onOpenChange={(o) => !o && setDestroy(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {destroy === "ALL"
                    ? `Remove all products from ${region.display_name}?`
                    : `Remove ${destroy?.name} from ${region.display_name}?`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Offerings with no purchases are deleted; ones already sold are
                  deactivated (kept as history). The product itself and existing
                  purchases are not affected.
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDestroy(null)}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={runDestroy}
                    disabled={destroyPending}
                  >
                    {destroyPending ? "Removing…" : "Remove"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </ContentLayout>
  );
}
